// DataChannel 控制通道消息协议（主进程、渲染进程共用）。
import type { RemoteStreamSettings, StreamFrameRate, StreamQuality } from './stream-profile';

export type MouseButton = 'left' | 'right' | 'middle';

/** 只为需要确认“已经执行”的操作生成回执；鼠标移动和滚轮仍保持轻量实时路径。 */
export type InputAckableType = 'mousedown' | 'mouseup' | 'click' | 'keydown' | 'keyup';

// 大文本剪贴板会在可靠 DataChannel 中形成很大的单条消息，可能阻塞输入事件。
// 保留常规开发文本，同时给视频和控制通道留出余量。
export const MAX_CLIPBOARD_LENGTH = 512 * 1024;

export interface SessionHeartbeatMessage {
  type: 'session-heartbeat';
  ts?: number;
  // 可选字段保持旧版客户端兼容；缺少该字段表示 legacy v0。
  protocolVersion?: number;
}

export interface SessionHeartbeatAckMessage {
  type: 'session-heartbeat-ack';
  ts?: number;
  protocolVersion?: number;
}

export interface ClipboardWriteMessage {
  type: 'clipboard';
  text: string;
  // 富文本只保留经过清洗的常见排版；text 始终作为兼容回退存在。
  html?: string;
  // RTF 仅作为桌面端增强格式；不支持的环境会忽略它并使用 HTML/纯文本。
  rtf?: string;
  // 新版剪贴板通道使用 transferId 做端到端回执；可选字段保持旧版本兼容。
  transferId?: string;
}

export interface ClipboardReadMessage {
  type: 'clipboard-read';
  // 控制端请求被控端当前剪贴板时，用 requestId 把响应和请求对应起来。
  requestId?: string;
}

export interface ClipboardAckMessage {
  type: 'clipboard-ack';
  transferId: string;
  ok: boolean;
  reason?: string;
}

export interface VideoSettingsMessage extends RemoteStreamSettings {
  type: 'video-settings';
}

export interface VideoSettingsAckMessage {
  type: 'video-settings-ack';
  ok: boolean;
  quality: StreamQuality;
  frameRate: StreamFrameRate;
  automatic?: boolean;
  width?: number;
  height?: number;
  appliedFrameRate?: number;
  reason?: string;
}

export type ClipboardControlMessage = ClipboardWriteMessage | ClipboardReadMessage;

export type InputMessage =
  | { type: 'mousemove'; x: number; y: number } // 0..1 归一化坐标
  // 按下消息携带坐标，避免无序的鼠标移动通道尚未到达时，按钮在旧位置触发。
  // 坐标字段保持可选，以兼容移动端和旧版 Client。
  | { type: 'mousedown'; button: MouseButton; x?: number; y?: number; id?: string }
  | { type: 'mouseup'; button: MouseButton; x?: number; y?: number; id?: string }
  // 手机轻触使用带坐标的原子点击，避免可丢弃 mousemove 与可靠按钮通道
  // 乱序；PC 控制端仍发送 mousedown/mouseup，保留真实按住和双击节奏。
  | { type: 'click'; button: MouseButton; x: number; y: number; id?: string }
  | { type: 'wheel'; deltaX: number; deltaY: number }
  | { type: 'keydown'; key: string; id?: string; repeat?: boolean }
  | { type: 'keyup'; key: string; id?: string }
  // 连接恢复/控制通道重新打开时，用一份当前状态校准远端原生输入。
  // 它不是事件队列：只描述“现在应该按住什么”，用于修复断线边界的
  // keyup/mouseup 丢失和旧会话残留，不参与高频鼠标移动路径。
  | { type: 'input-state'; keys: string[]; buttons: MouseButton[] }
  | SessionHeartbeatMessage
  | VideoSettingsMessage
  | ClipboardControlMessage;

export type InputAckMessage = {
  type: 'input-ack';
  id: string;
  inputType: InputAckableType;
  ok: boolean;
};

// 被控端真实光标的透明小图。它走独立的有序 DataChannel，避免光标状态
// 更新占用键盘、鼠标和剪贴板控制通道；光标是“当前状态”而不是事件，不能
// 使用 maxRetransmits: 0，否则一次丢包会让控制端永久停留在旧形态。
export interface RemoteCursorMessage {
  type: 'cursor';
  visible: boolean;
  data?: string;
  width?: number;
  height?: number;
  hotspotX?: number;
  hotspotY?: number;
  sourceWidth?: number;
  sourceHeight?: number;
}

export interface RemoteCursorModeMessage {
  type: 'cursor-mode';
  mode: 'captured' | 'overlay';
}

export type HostMessage =
  | ClipboardWriteMessage
  | ClipboardAckMessage
  | SessionHeartbeatAckMessage
  | VideoSettingsAckMessage
  | InputAckMessage
  | {
      type: 'input-permission';
      status: 'authorized' | 'denied' | 'restricted' | 'not-determined' | 'unsupported';
    }
  | { type: 'screen'; width: number; height: number }
  | RemoteCursorMessage
  | RemoteCursorModeMessage;
